import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  CircularProgress,
  Typography,
  Tabs,
  Tab,
  Container,
  styled,
  alpha,
} from '@mui/material';
import DescriptionIcon from '@mui/icons-material/Description';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import TripDetails from './TripDetails';
import AnalyticsPanel from './AnalyticsPanel';
import TripSelector from './TripSelector';

// Styled Paper for Tabs
const TabsPaper = styled(Paper)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  borderRadius: theme.shape.borderRadius * 1.5,
  boxShadow: `0 4px 12px ${alpha(theme.palette.common.black, 0.05)}`,
  overflow: 'hidden',
}));

// Styled Tab
const StyledTab = styled(Tab)(({ theme }) => ({
  minHeight: 56,
  padding: theme.spacing(1, 3),
  fontWeight: 500,
  '&.Mui-selected': {
    color: theme.palette.primary.main,
  },
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.04),
  }
}));

const Dashboard = ({ tripData }) => {
  const [loading, setLoading] = useState(true);
  const [processedTripData, setProcessedTripData] = useState([]);
  const [selectedTrip, setSelectedTrip] = useState(null);
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    const prepareData = async () => {
      setLoading(true);
      if (!tripData || tripData.length === 0) {
         setProcessedTripData([]);
         setSelectedTrip(null);
         setLoading(false);
         return;
      }

      try {
        const enrichedTrips = tripData.map((trip, index) => {
          const enrichedTrip = {
            ...trip,
            uniqueKey: `${trip.id}-${index}`,
          };

          if (trip.segments) {
              enrichedTrip.segments = trip.segments;
          }

          return enrichedTrip;
        });

        setProcessedTripData(enrichedTrips);
        if (enrichedTrips.length > 0) {
          setSelectedTrip(enrichedTrips[0]);
        } else {
          setSelectedTrip(null);
        }
      } catch (error) {
        console.error("Error preparing data:", error);
        setProcessedTripData([]);
        setSelectedTrip(null);
      } finally {
        setLoading(false);
      }
    };

    prepareData();
  }, [tripData]);

  const handleTripSelect = (trip) => {
    setSelectedTrip(trip);
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '80vh', gap: 2 }}>
        <CircularProgress size={50} />
        <Typography variant="h6" color="text.secondary">
          Preparing trip data...
        </Typography>
      </Box>
    );
  }

  const tripsByMonth = activeTab === 1 ? processedTripData.reduce((acc, trip) => {
    const date = new Date(trip.startTime);
    if (isNaN(date.getTime())) return acc;
    const monthYear = `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}`;
    if (!acc[monthYear]) acc[monthYear] = [];
    acc[monthYear].push(trip);
    return acc;
  }, {}) : {};


  return (
    <Container maxWidth="xl" sx={{ pt: 4, pb: 4 }}>
      <TabsPaper elevation={0} variant="outlined">
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="fullWidth"
          textColor="primary"
          indicatorColor="primary"
          aria-label="Dashboard tabs"
        >
          <StyledTab icon={<DescriptionIcon />} iconPosition="start" label="Trip Details" />
          <StyledTab icon={<AnalyticsIcon />} iconPosition="start" label="Analytics" />
        </Tabs>
      </TabsPaper>

      {/* Tab 0: Trip Details */}
      <Box sx={{ visibility: activeTab === 0 ? 'visible' : 'hidden', height: activeTab === 0 ? 'auto' : 0, overflow: 'hidden' }}>
        {/* Use processedTripData length check */}
        {processedTripData.length === 0 ? (
           <Paper sx={{ p: 4, textAlign: 'center', mt: 4, backgroundColor: (theme) => alpha(theme.palette.background.paper, 0.7) }}>
              <Typography variant="h6" color="text.secondary">No trip data available to display.</Typography>
           </Paper>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <TripSelector
              trips={processedTripData}
              selectedTrip={selectedTrip}
              onSelectTrip={handleTripSelect}
            />
            <Paper
              elevation={0}
              variant="outlined"
              sx={(theme) => ({
                p: 0,
                borderRadius: theme.shape.borderRadius * 1.5,
                bgcolor: theme.palette.background.default,
              })}
            >
              <TripDetails
                trip={selectedTrip}
                allTrips={processedTripData}
                sx={{}}
              />
            </Paper>
          </Box>
        )}
      </Box>


      {/* Tab 1: Analytics */}
      <Box sx={{ visibility: activeTab === 1 ? 'visible' : 'hidden', height: activeTab === 1 ? 'auto' : 0, overflow: 'hidden' }}>
         {processedTripData.length === 0 ? (
             <Paper sx={{ p: 4, textAlign: 'center', mt: 4, backgroundColor: (theme) => alpha(theme.palette.background.paper, 0.7) }}>
                <Typography variant="h6" color="text.secondary">No trip data available for analytics.</Typography>
             </Paper>
          ) : (
             <AnalyticsPanel tripData={processedTripData} tripsByMonth={tripsByMonth} />
          )}
      </Box>
    </Container>
  );
};

export default Dashboard;